import { useQuery, useMutation, useQueryClient } from "react-query";
import { deleteEstatesParent, getEstatesParent, getEstatesParents, getEstatesParentsLookups, saveEstatesParent } from "@/api/Maintainers/estates_parents";
import { useApplyCreatedEstateAccess } from "@/hooks/general/useApplyCreatedEstateAccess";
import { buildEstatesAccessEntries, mergeListFiltersWithNewIds, parseEstatesFromForm } from "@/utils/estateFilterAccess";

export const key = "estates-parents";

export function useEstatesParentsLookups(options = {}) {
	return useQuery([key, "lookups"], () => getEstatesParentsLookups(), options);
}

export function useEstatesParentsList(filters, options = {}) {
	return useQuery([key, "list", filters], () => getEstatesParents(filters), options);
}

export function useEstatesParentDetail(slug, enabled) {
	return useQuery([key, "detail", slug], () => slug && getEstatesParent(slug), {
		enabled: enabled && !!slug,
		keepPreviousData: true,
	});
}

export function useSaveEstatesParent() {
	const queryClient = useQueryClient();
	const { applyCreatedEstateAccess } = useApplyCreatedEstateAccess();

	return useMutation(({ form, filters }) => saveEstatesParent(form, filters), {
		onSuccess: (response, variables) => {
			const isCreate = !variables.form?.id;
			let listFilters = variables.filters;

			const parsedFormEstates = parseEstatesFromForm(variables.form?.estates);
			const newEstateNames = [...(variables.form?.new_estate_names ?? []), ...parsedFormEstates.newNames];
			const shouldSyncAccess = Boolean(response?.detail?.id) && (isCreate || newEstateNames.length > 0 || parsedFormEstates.existingIds.length > 0);

			if (shouldSyncAccess) {
				const estateIdsFromDetail = response.detail.estates ?? response.saved_estate_ids ?? [];
				const estateIds = [...new Set([...estateIdsFromDetail, ...parsedFormEstates.existingIds, ...(response.saved_estate_ids ?? [])])];

				listFilters = mergeListFiltersWithNewIds(variables.filters, {
					estateParentId: response.detail.id,
					estateIds,
				});

				const lookups = queryClient.getQueryData([key, "lookups"]);
				const estateOptions = lookups?.data?.estates ?? [];
				const savedEstateLabels = {};

				(response?.saved_estates ?? []).forEach((estate) => {
					const id = Number(estate.id ?? estate.value);
					const label = estate.label ?? estate.name;
					if (!Number.isNaN(id) && id > 0 && label) {
						savedEstateLabels[id] = label;
					}
				});

				const estatesAccess = buildEstatesAccessEntries({
					estateIds,
					estateParentId: response.detail.id,
					estateOptions,
					newNames: newEstateNames,
					savedEstateLabels,
				});

				applyCreatedEstateAccess({
					estateParent: { value: response.detail.id, label: response.detail.name, show_estate: true },
					estates: estatesAccess,
				});
			}

			if (response?.data && listFilters) {
				queryClient.setQueryData([key, "list", listFilters], response);
			}

			if (response?.detail?.slug) {
				queryClient.setQueryData([key, "detail", response.detail.slug], { data: response.detail });
			}

			queryClient.invalidateQueries([key, "lookups"]);
		},
	});
}

export function useDeleteEstatesParent() {
	const queryClient = useQueryClient();

	return useMutation(deleteEstatesParent, {
		onSuccess: () => {
			queryClient.invalidateQueries([key, "list"]);
			queryClient.invalidateQueries([key, "lookups"]);
		},
	});
}
